<template>
    <pc-alert
        v-if="redirectLanding"
        v-bind:severity="ALERT_SEVERITY.warning"
        shown>
        {{ redirectWarningMessage }}
    </pc-alert>
    <div class="mb-3">
        <h2>
            {{ existingProgramsSubheader }}
        </h2>
        <div>
            <pc-table
                v-bind:items="existingProgramsMapping"
                v-bind:columns="existingProgramCols"
                v-bind:empty-text="existingProgramsTableEmptyText"
                hide-search
                key-prop="ProgramId">
                <template v-slot:ProgramName="ProgramNameColumn">
                    <div class="d-flex justify-content-between">
                        <div>
                            {{ ProgramNameColumn.ProgramName }}
                        </div>
                        <pc-button v-on:click.stop="gotoProgramHomepage(ProgramNameColumn)">
                            {{ goToProgramButtonText }}
                        </pc-button>
                    </div>
                </template>
            </pc-table>
        </div>
    </div>
    <div class="mb-3">
        <h2>{{ programToAddSubheader }}</h2>
        <hr />
        <div
            v-if="redirectLanding"
            class="fst-italic mb-3">
            {{ redirectProgramToAddInstructionText }}
        </div>
        <div
            v-else
            class="fst-italic mb-3">
            {{ programToAddInstructionText }}
        </div>
        <div class="row">
            <div class="col col-12 col-lg-6">
                <div v-if="availableAgenciesLength === 0">
                    {{ noAvailableProgramsText }}
                </div>
                <div v-else>
                    <pc-select
                        v-model="selectedAgencyName"
                        v-bind:label="agencySelectLabel"
                        v-bind:options="availableAgencies"
                        v-bind:readonly="hasOneAgency"
                        class="mb-3">
                        <option
                            v-if="!hasOneAgency"
                            value="">
                            {{ agencySelectDefaultOptionText }}
                        </option>
                    </pc-select>
                    <pc-transition transition="slide">
                        <div v-if="selectedAgencyName">
                            <pc-select
                                v-model="selectedProgramId"
                                v-bind:label="programSelectLabel"
                                v-bind:options="availableProgramsForAgency"
                                v-bind:readonly="hasOneProgram"
                                class="mb-3">
                                <option
                                    v-if="!hasOneProgram"
                                    value="">
                                    {{ programSelectDefaultOptionText }}
                                </option>
                            </pc-select>
                        </div>
                    </pc-transition>
                    <pc-transition transition="slide">
                        <div v-if="selectedProgramId">
                            <pc-select
                                v-model="selectedRoleId"
                                v-bind:label="roleSelectLabel"
                                v-bind:options="availableRolesForProgram"
                                v-bind:readonly="hasOneRole"
                                required
                                class="mb-3">
                                <option
                                    v-if="!hasOneRole"
                                    value="">
                                    {{ roleSelectDefaultOptionText }}
                                </option>
                            </pc-select>
                        </div>
                    </pc-transition>
                </div>
            </div>
        </div>
        <div class="d-flex align-items-center justify-content-end">
            <pc-loader
                small
                v-bind:loading="saving">
                <pc-button
                    primary
                    v-bind:disabled="!canSave"
                    v-on:click="onAddProgram">
                    {{ addProgramBtnLabel }}
                </pc-button>
            </pc-loader>
        </div>
    </div>
    <pc-alert
        v-bind="alertBinder"
        v-model:shown="showAlert" />
</template>

<script setup>
    import { KEYS, NAMESPACE } from "./i18n/addPrograms.i18n";
    import { useResource } from "Util/i18n";
    import { ALERT_DURATION, ALERT_SEVERITY } from "Util/alerts";
    import { ref, computed, onMounted } from "vue";

    const REDIRECT_DELAY = 250;
    const saving = ref(false);

    const selectedAgencyNameInternal = ref(null);
    const selectedProgramIdInternal = ref(null);
    const selectedRoleId = ref(null);

    const showAlert = ref(false);
    const alertMessage = ref(null);
    const alertDuration = ref(ALERT_DURATION.long);
    const alertSeverity = ref(ALERT_SEVERITY.success);

    const props = defineProps({
        existingPrograms: { type: Array, required: true },
        availableProgramsByAgency: { type: Object, required: true },
        addProgram: { type: Function, required: true },
        page: { type: Object, required: true },
        redirectLanding: { type: Boolean, default: false },
        baseProgramUrl: { type: String, required: true },
        redirecting: { type: Boolean, default: false }
    });

    const alertBinder = computed(() => {
        return {
            message: alertMessage.value,
            duration: alertDuration.value,
            severity: alertSeverity.value
        };
    });
    const selectedAgencyName = computed({
        get() {
            return selectedAgencyNameInternal.value;
        },
        set(input) {
            selectedAgencyNameInternal.value = input;
            onAgencyChanged();
        }
    });
    const selectedProgramId = computed({
        get() {
            return selectedProgramIdInternal.value;
        },
        set(input) {
            selectedProgramIdInternal.value = input;
            onProgramChanged();
        }
    });
    const existingProgramCols = computed(() => {
        return [
            {
                header: existingProgramsAgencyColumnHeader.value,
                property: "AgencyName",
                visible: true,
                orderable: true,
                searchable: true
            },
            {
                header: existingProgramsProgramColumnHeader.value,
                property: "ProgramName",
                visible: true,
                orderable: true,
                searchable: true,
                isCardHeader: true
            },
            {
                header: existingProgramsRoleColumnHeader.value,
                property: "RoleName",
                visible: true,
                orderable: true,
                searchable: true
            }
        ];
    });
    const existingProgramsMapping = computed(() => {
        return props.existingPrograms.map(_programRoleInfoToExistingProgram);
    });
    const availableAgencies = computed(() => {
        const agencies = [];
        for (const agencyName in props.availableProgramsByAgency) {
            if (
                props.availableProgramsByAgency.hasOwnProperty(agencyName) &&
                props.availableProgramsByAgency[agencyName].length > 0
            ) {
                agencies.push({
                    text: agencyName,
                    value: agencyName
                });
            }
        }
        return agencies.sort((a1, a2) => {
            if (a1.text < a2.text) {
                return -1;
            }
            if (a1.text === a2.text) {
                return 0;
            }
            return 1;
        });
    });
    const availableAgenciesLength = computed(() => availableAgencies.value.length);
    const availableProgramsForAgency = computed(() => {
        if (!selectedAgencyName.value) {
            return [];
        }

        const programMappingsForAgency = props.availableProgramsByAgency[selectedAgencyName.value];
        return programMappingsForAgency.map((programRoleInfo) => {
            return {
                text: programRoleInfo.Program.Name,
                value: programRoleInfo.Program.Id
            };
        });
    });
    const availableRolesForProgram = computed(() => {
        if (!selectedProgramId.value || !selectedAgencyName.value) {
            return [];
        }

        const selectedProgramMapping = props.availableProgramsByAgency[selectedAgencyName.value].find(
            (programRoleInfo) => {
                return programRoleInfo.Program.Id === selectedProgramId.value;
            }
        );
        return selectedProgramMapping.Roles.map((r) => {
            return {
                text: r.Name,
                value: r.Id
            };
        });
    });
    const hasOneAgency = computed(() => {
        return availableAgencies.value.length === 1;
    });
    const hasOneProgram = computed(() => {
        return availableProgramsForAgency.value.length === 1;
    });
    const hasOneRole = computed(() => {
        return availableRolesForProgram.value.length === 1;
    });
    const canSave = computed(() => {
        return selectedRoleId.value != "" && selectedRoleId.value != null && !saving.value && !props.redirecting;
    });

    const {
        existingProgramsSubheader,
        existingProgramsTableEmptyText,
        existingProgramsAgencyColumnHeader,
        existingProgramsProgramColumnHeader,
        existingProgramsRoleColumnHeader,
        programToAddSubheader,
        programToAddInstructionText,
        noAvailableProgramsText,
        agencySelectLabel,
        agencySelectDefaultOptionText,
        programSelectLabel,
        programSelectDefaultOptionText,
        roleSelectLabel,
        roleSelectDefaultOptionText,
        addProgramBtnLabel,
        addProgramSuccessAlertText,
        redirectProgramToAddInstructionText,
        redirectWarningMessage,
        goToProgramButtonText
    } = useResource(KEYS, NAMESPACE);

    const emit = defineEmits(["program-added"]);

    onMounted(() => {
        if (hasOneAgency.value) {
            selectedAgencyName.value = availableAgencies.value[0].value;
        }
    });

    function onAvailableAgenciesChange() {
        selectedAgencyName.value = null;

        if (hasOneAgency.value) {
            selectedAgencyName.value = availableAgencies.value[0].value;
            onAgencyChanged();
        }
    }

    function onAgencyChanged() {
        selectedProgramId.value = null;

        if (hasOneProgram.value) {
            selectedProgramId.value = availableProgramsForAgency.value[0].value;
            onProgramChanged();
        }
    }

    function onProgramChanged() {
        if (hasOneRole.value) {
            selectedRoleId.value = availableRolesForProgram.value[0].value;
        } else {
            selectedRoleId.value = null;
        }
    }

    async function onAddProgram() {
        saving.value = true;

        const agencyName = selectedAgencyName.value;
        const selectedProgram = selectedProgramId.value;
        const selectedRole = selectedRoleId.value;

        let availableMappingsForAgency = props.availableProgramsByAgency[agencyName];
        try {
            const result = await props.addProgram({
                currentProgramId: props.page.ProgramId,
                programIdToAdd: selectedProgram,
                roleIdToAdd: selectedRole
            });
            if (result.status === "success") {
                saving.value = false;
                // Find the mapping that we just added so we can add it to the existing programs
                const addedProgramMapping = availableMappingsForAgency.find((mapping) => {
                    return mapping.Program.Id === selectedProgram;
                });
                // Filter roles list to the role the user selected when registering for the program
                addedProgramMapping.Roles = addedProgramMapping.Roles.filter((role) => role.Id === selectedRole);

                // Create mapping for this agency to remove the registered program from the available list
                availableMappingsForAgency = availableMappingsForAgency.filter((mapping) => {
                    return mapping.Program.Id !== selectedProgram;
                });

                // Set the parent mappings
                emit("program-added", {
                    addedProgramMapping,
                    agencyName,
                    availableMappingsForAgency
                });

                // Reset User Selections
                onAvailableAgenciesChange();

                _showAlert(addProgramSuccessAlertText.value);

                if (result.Data?.ProgramAccessRevokedRedirect) {
                    setTimeout(() => {
                        props.page.replaceHref(result.Data.ProgramAccessRevokedRedirect);
                    }, REDIRECT_DELAY);
                }
            }
        } finally {
            saving.value = false;
        }
    }

    function _programRoleInfoToExistingProgram(programRoleInfo) {
        if (programRoleInfo.Roles.length != 1) {
            // eslint-disable-next-line max-len
            throw "Cannot convert program role info to existing program; user must have exactly 1 role in the specified program.";
        }

        const programRole = programRoleInfo.Roles[0];

        return {
            AgencyName: programRoleInfo.Program.AgencyName,
            ProgramName: programRoleInfo.Program.Name,
            ProgramId: programRoleInfo.Program.Id,
            RoleName: programRole.Name,
            RoleId: programRole.Id
        };
    }

    function _showAlert(message, severity) {
        showAlert.value = true;
        alertMessage.value = message;
        alertSeverity.value = severity || ALERT_SEVERITY.success;
    }

    function gotoProgramHomepage(ProgramNameColumn) {
        props.page.setHref(props.baseProgramUrl + ProgramNameColumn.ProgramId);
    }
</script>
